import { getApiKeys, createApiKey, deleteApiKey } from '../admin-api';
import { esc, showConfirm, formField, formatDate } from '../admin-ui';
import type { ApiKeyRow } from '../admin-api';
import { cmsErrorText } from './admin-cms';

export async function mountApiKeys(container: HTMLElement): Promise<() => void> {
  let alive = true;
  let keys: ApiKeyRow[] = [];
  let revealedKey: string | null = null;

  async function load(): Promise<void> {
    if (!alive) return;
    try {
      keys = await getApiKeys();
    } catch {
      container.innerHTML = `<p class="text-red-400 p-4">Failed to load API keys.</p>`;
      return;
    }
    render();
  }

  function render(): void {
    container.innerHTML = `
      <div class="flex items-center justify-between mb-4">
        <h2 class="font-heading text-gold text-lg">API Keys</h2>
        <button id="adm-key-add" class="btn btn-gold min-h-11 px-4 text-sm">Create Key</button>
      </div>

      <div id="adm-key-form" class="hidden frame-pewter p-4 mb-4">
        ${formField('Key Name', 'adm-key-name', 'text')}
        <div class="flex gap-2 mt-3">
          <button id="adm-key-save" class="btn btn-gold min-h-11 px-4 text-sm">Create</button>
          <button id="adm-key-cancel" class="btn btn-stone min-h-11 px-4 text-sm">Cancel</button>
        </div>
      </div>

      ${revealedKey ? `
        <div class="frame-raised p-4 mb-4 bg-amber-950/30 border border-gold/40">
          <p class="text-sm text-gold font-heading mb-2">New API Key (shown once)</p>
          <code class="block p-2 bg-surface-2 text-text text-xs break-all rounded select-all">${esc(revealedKey)}</code>
          <button id="adm-key-dismiss" class="btn btn-stone min-h-11 px-3 text-xs mt-2">Dismiss</button>
        </div>
      ` : ''}

      <div class="frame-pewter overflow-x-auto">
        <table class="w-full text-sm text-left">
          <thead>
            <tr class="border-b border-border text-muted">
              <th class="px-3 py-2">Name</th>
              <th class="px-3 py-2">Created</th>
              <th class="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            ${keys.length === 0
              ? `<tr><td colspan="3" class="px-3 py-6 text-center text-muted">No API keys.</td></tr>`
              : keys.map((k) => `
                <tr class="border-b border-border/50 hover:bg-surface-2/50">
                  <td class="px-3 py-2">${esc(k.name)}</td>
                  <td class="px-3 py-2 whitespace-nowrap">${formatDate(k.createdAt)}</td>
                  <td class="px-3 py-2 text-right">
                    <button class="adm-key-del btn btn-stone min-h-11 px-3 text-xs text-red-400" data-id="${esc(k.id)}">Delete</button>
                  </td>
                </tr>
              `).join('')}
          </tbody>
        </table>
      </div>
    `;

    wire();
  }

  /** Failures used to vanish into an empty catch; they now show above the list. */
  function showKeyError(message: string): void {
    let slot = container.querySelector<HTMLElement>('#adm-key-error');
    if (!slot) {
      slot = document.createElement('p');
      slot.id = 'adm-key-error';
      slot.setAttribute('role', 'alert');
      slot.className = 'text-sm text-red-400 mb-3';
      container.prepend(slot);
    }
    slot.textContent = message;
  }

  function wire(): void {
    const form = container.querySelector<HTMLElement>('#adm-key-form')!;

    container.querySelector('#adm-key-add')?.addEventListener('click', () => {
      (container.querySelector<HTMLInputElement>('#adm-key-name'))!.value = '';
      form.classList.remove('hidden');
    });
    container.querySelector('#adm-key-cancel')?.addEventListener('click', () => form.classList.add('hidden'));

    container.querySelector('#adm-key-save')?.addEventListener('click', async () => {
      const name = (container.querySelector<HTMLInputElement>('#adm-key-name'))!.value.trim();
      if (!name) return;
      try {
        const result = await createApiKey({ name });
        revealedKey = result.key;
        keys = await getApiKeys();
        render();
      } catch (err) {
        showKeyError(cmsErrorText(err));
      }
    });

    container.querySelector('#adm-key-dismiss')?.addEventListener('click', () => {
      revealedKey = null;
      render();
    });

    container.querySelectorAll<HTMLButtonElement>('.adm-key-del').forEach((btn) => {
      btn.addEventListener('click', () => {
        showConfirm('Delete this API key? This cannot be undone.', async () => {
          try {
            await deleteApiKey(btn.dataset.id!);
            keys = await getApiKeys();
            render();
          } catch (err) {
            showKeyError(cmsErrorText(err));
          }
        });
      });
    });
  }

  await load();
  return () => { alive = false; };
}
